import VariantPicker from '@theme/variant-picker';
import { Component } from '@theme/component';
import { debounce, isDesktopBreakpoint, mediaQueryLarge } from '@theme/utilities';
import { ThemeEvents, VariantSelectedEvent, VariantUpdateEvent, SlideshowSelectEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * A custom element that displays a product card.
 *
 * @typedef {object} Refs
 * @property {HTMLAnchorElement} productCardLink - The product card link element.
 * @property {import('slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {import('quick-add').QuickAddComponent} [quickAdd] - The quick add component.
 * @property {HTMLElement} [cardGallery] - The card gallery component.
 *
 * @extends {Component<Refs>}
 */
export class ProductCard extends Component {
  requiredRefs = ['productCardLink'];

  get productPageUrl() {
    return this.refs.productCardLink.href;
  }

  #fetchProductPageHandler = () => {
    if (!this.refs.quickAdd?.cachedProductHtml) {
      this.refs.quickAdd?.fetchProductPage(this.productPageUrl);
    }
  };

  /**
   * Navigates to a URL link. Respects modifier keys for opening in new tab/window.
   * @param {Event} event - The event that triggered the navigation.
   * @param {URL} url - The URL to navigate to.
   */
  #navigateToURL = (event, url) => {
    // Check for modifier keys that should open in new tab/window (only for mouse events)
    const shouldOpenInNewTab =
      event instanceof MouseEvent && (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1);

    if (shouldOpenInNewTab) {
      event.preventDefault();
      window.open(url.href, '_blank');
      return;
    } else {
      window.location.href = url.href;
    }
  };

  connectedCallback() {
    super.connectedCallback();

    const link = this.refs.productCardLink;
    if (!(link instanceof HTMLAnchorElement)) throw new Error('Product card link not found');
    this.#handleQuickAdd();

    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);
    mediaQueryLarge.addEventListener('change', this.#handleQuickAdd);

    this.addEventListener('click', this.navigateToProduct);

    // Preload the next image on the slideshow to avoid white flashes on previewImage
    setTimeout(() => {
      if (this.refs.slideshow?.isNested) {
        this.#preloadNextPreviewImage();
      }
    });

    // FIX: preload the (hidden + lazy) variant images once the card gets close to the viewport,
    // so switching swatches does not show a white/empty image while the photo downloads.
    this.#observeForVariantPreload();

    // FIX: keep the cover stable on every refresh. Safari (iPhone) can re-snap the card gallery to another slide
    // while images load or when it restores the page, so the cover looked random after a refresh.
    // Until the user picks a swatch, we pin the gallery to its first visible slide.
    setTimeout(this.#pinInitialSlide);
    window.addEventListener('load', this.#pinInitialSlide, { once: true });
    window.addEventListener('pageshow', this.#pinInitialSlide);
    setTimeout(this.#pinInitialSlide, 1500);

    // FIX: card-gallery.liquid shows ONLY the cover until [data-gallery-ready] is set.
    // We set it on the first real interaction with the card (hover with mouse, touch, keyboard focus).
    this.addEventListener('pointerenter', this.#markGalleryReady, { capture: true });
    this.addEventListener('pointerdown', this.#markGalleryReady, { capture: true });
    this.addEventListener('focusin', this.#markGalleryReady);
  }

  /**
   * Shows the rest of the gallery slides (they are display:none until the user interacts with the card).
   */
  #markGalleryReady = () => {
    const gallery = this.refs.cardGallery || this.querySelector('.card-gallery');
    if (!gallery || gallery.hasAttribute('data-gallery-ready')) return;

    gallery.setAttribute('data-gallery-ready', '');

    // Adding slides back can make the browser re-snap: keep the current cover in place.
    if (!this.#userChangedVariant) {
      this.#pinInitialSlide();
      requestAnimationFrame(() => this.#pinInitialSlide());
    }
  };

  /** True after the user changed the swatch (then we must not touch the gallery anymore). */
  #userChangedVariant = false;

  #pinInitialSlide = () => {
    if (this.#userChangedVariant || !this.isConnected) return;

    const { slideshow } = this.refs;
    const firstSlide = slideshow?.refs?.slides?.find((slide) => !slide.hasAttribute('hidden'));
    if (!firstSlide) return;

    this.#showSlide(firstSlide);
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.navigateToProduct);
    window.removeEventListener('load', this.#pinInitialSlide);
    window.removeEventListener('pageshow', this.#pinInitialSlide);
    this.removeEventListener('pointerenter', this.#markGalleryReady, { capture: true });
    this.removeEventListener('pointerdown', this.#markGalleryReady, { capture: true });
    this.removeEventListener('focusin', this.#markGalleryReady);
    this.#variantPreloadObserver?.disconnect();
    this.#variantPreloadObserver = undefined;
  }

  /** @type {IntersectionObserver | undefined} */
  #variantPreloadObserver;

  /** Increases on every preview / reset so an old (slow) image load can't override a newer choice. */
  #previewToken = 0;

  /**
   * Starts loading all variant images of this card when the card is near the viewport.
   */
  #observeForVariantPreload() {
    if (!('IntersectionObserver' in window)) {
      this.#preloadVariantImages();
      return;
    }

    this.#variantPreloadObserver = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          this.#preloadVariantImages();
          this.#variantPreloadObserver?.disconnect();
          this.#variantPreloadObserver = undefined;
        }
      },
      { rootMargin: '300px' }
    );

    this.#variantPreloadObserver.observe(this);
  }

  /**
   * Removes loading="lazy" from the variant slides' images.
   * Lazy images inside hidden slides (display: none) are never downloaded until the slide is shown,
   * which is what caused the empty image when a swatch was selected.
   */
  #preloadVariantImages() {
    const slides = this.refs.slideshow?.refs?.slides ?? [];

    for (const slide of slides) {
      if (slide.getAttribute('variant-image') == null) continue;
      slide.querySelector('img[loading="lazy"]')?.removeAttribute('loading');
    }
  }

  /**
   * Finds a slide by its media id.
   * @param {string | number} id
   * @returns {HTMLElement | undefined}
   */
  #getSlideById(id) {
    const slides = this.refs.slideshow?.refs?.slides ?? [];
    return slides.find((slide) => slide.getAttribute('slide-id') == id);
  }

  /**
   * Waits until the image inside a slide is downloaded (max 4 seconds).
   * @param {HTMLElement | undefined} slide
   * @returns {Promise<void>}
   */
  async #waitForSlideImage(slide) {
    const img = slide?.querySelector('img');
    if (!(img instanceof HTMLImageElement)) return;

    if (img.getAttribute('loading') === 'lazy') img.removeAttribute('loading');

    if (!(img.complete && img.naturalWidth > 0)) {
      await new Promise((resolve) => {
        const timeout = setTimeout(resolve, 4000);
        const done = () => {
          clearTimeout(timeout);
          resolve(undefined);
        };
        img.addEventListener('load', done, { once: true });
        img.addEventListener('error', done, { once: true });
      });
    }
    // Note: don't use img.decode() here — for an image inside a hidden slide (display: none)
    // the promise never resolves, which blocked the image switch completely.
  }

  #preloadNextPreviewImage() {
    const currentSlide = this.refs.slideshow?.slides?.[this.refs.slideshow?.current];
    currentSlide?.nextElementSibling?.querySelector('img[loading="lazy"]')?.removeAttribute('loading');
  }

  /**
   * Handles the quick add event.
   */
  #handleQuickAdd = () => {
    this.removeEventListener('pointerenter', this.#fetchProductPageHandler);
    this.removeEventListener('focusin', this.#fetchProductPageHandler);

    if (isDesktopBreakpoint()) {
      this.addEventListener('pointerenter', this.#fetchProductPageHandler);
      this.addEventListener('focusin', this.#fetchProductPageHandler);
    }
  };

  /**
   * Handles the variant selected event.
   * @param {VariantSelectedEvent} event - The variant selected event.
   */
  #handleVariantSelected = (event) => {
    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateSelectedOption(event.detail.resource.id);
    }
  };

  /**
   * Handles the variant update event.
   * Updates price, checks for unavailable variants, and updates product URL.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    // Stop the event from bubbling up to the section, variant updates triggered from product cards are fully handled
    // by this component and should not affect anything outside the card.
    event.stopPropagation();

    this.#userChangedVariant = true;
    this.#markGalleryReady();
    this.updatePrice(event);
    this.#isUnavailableVariantSelected(event);
    this.#updateProductUrl(event);
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);

    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateVariantPicker(event.detail.data.html);
    }

    this.#updateVariantImages();
    this.#previousSlideIndex = null;
  };

  /**
   * Updates the DOM with a new price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice(event) {
    const priceContainer = this.querySelectorAll(`product-price [ref='priceContainer']`)[1];
    const newPriceElement = event.detail.data.html.querySelector(`product-price [ref='priceContainer']`);

    if (newPriceElement && priceContainer) {
      morph(priceContainer, newPriceElement);
    }
  }

  /**
   * Updates the product URL based on the variant update event.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #updateProductUrl(event) {
    const anchorElement = event.detail.data.html?.querySelector('product-card a');
    const featuredMediaUrl = event.detail.data.html
      ?.querySelector('product-card-link')
      ?.getAttribute('data-featured-media-url');

    // If the product card is inside a product link, update the product link's featured media URL
    if (featuredMediaUrl && this.closest('product-card-link'))
      this.closest('product-card-link')?.setAttribute('data-featured-media-url', featuredMediaUrl);

    if (anchorElement instanceof HTMLAnchorElement) {
      // If the href is empty, don't update the product URL eg: unavailable variant
      if (anchorElement.getAttribute('href')?.trim() === '') return;

      this.refs.productCardLink.href = anchorElement.href;
    }
  }

  /**
   * Checks if an unavailable variant is selected.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #isUnavailableVariantSelected(event) {
    const allVariants = /** @type {NodeListOf<HTMLInputElement>} */ (
      event.detail.data.html.querySelectorAll('input:checked')
    );

    for (const variant of allVariants) {
      this.#toggleAddToCartButton(variant.dataset.optionAvailable === 'true');
    }
  }

  /**
   * Toggles the add to cart button state.
   * @param {boolean} enable - Whether to enable or disable the button.
   */
  #toggleAddToCartButton(enable) {
    const addToCartButton = this.querySelector('.add-to-cart__button button');

    if (addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = !enable;
    }
  }

  /**
   * Hide the variant images that are not for the selected variant.
   */
  async #updateVariantImages() {
    const { slideshow } = this.refs;
    if (!this.variantPicker?.selectedOption) {
      return;
    }

    const selectedImageId = this.variantPicker?.selectedOption.dataset.optionMediaId;

    if (slideshow && selectedImageId) {
      // FIX: keep the current image on screen until the new variant image is downloaded
      const token = ++this.#variantToken;
      const selectedSlide = this.#getSlideById(selectedImageId);
      await this.#waitForSlideImage(selectedSlide);
      if (token !== this.#variantToken) return;

      const { slides = [] } = slideshow.refs;

      for (const slide of slides) {
        if (slide.getAttribute('variant-image') == null) continue;

        slide.hidden = slide.getAttribute('slide-id') !== selectedImageId;
      }

      // FIX (main cause of the white image):
      // Inside a product-list carousel the card gallery slideshow is disabled, so slideshow.select()
      // does nothing and the newly shown slide keeps aria-hidden="true".
      // base.css gives nested slides with aria-hidden="true" `content-visibility: hidden`,
      // so the image is not painted (white box) until a scroll/resize event updates aria-hidden,
      // which on iPhone Safari can take seconds. We scroll to the slide and set aria-hidden ourselves.
      if (selectedSlide) this.#showSlide(selectedSlide);
    }
  }

  /** Separate token for variant changes (so pointerleave on mobile can't cancel them). */
  #variantToken = 0;

  /**
   * Makes a slide the visible one without relying on slideshow.select() (which is disabled in nested carousels).
   * @param {HTMLElement} targetSlide
   */
  #showSlide(targetSlide) {
    const { slideshow } = this.refs;
    if (!slideshow) return;

    const allSlides = slideshow.refs?.slides ?? [];
    for (const slide of allSlides) {
      slide.removeAttribute('reveal');
      slide.setAttribute('aria-hidden', `${slide !== targetSlide}`);
    }

    const scroller = slideshow.refs?.scroller;
    if (scroller instanceof HTMLElement) {
      const offset = targetSlide.getBoundingClientRect().left - scroller.getBoundingClientRect().left;
      if (Math.abs(offset) > 1) {
        scroller.scrollTo({ left: scroller.scrollLeft + offset, behavior: 'instant' });
      }
    }

    const index = slideshow.slides?.indexOf(targetSlide) ?? -1;
    if (index >= 0) {
      try {
        slideshow.current = index;
      } catch (error) {
        // ignore: current is only used for arrows/dots state
      }
    }
  }

  /**
   * Gets all variant inputs.
   * @returns {NodeListOf<HTMLInputElement>} All variant input elements.
   */
  get allVariants() {
    return this.querySelectorAll('input[data-variant-id]');
  }

  /**
   * Gets the variant picker component.
   * @returns {VariantPicker | null} The variant picker component.
   */
  get variantPicker() {
    return this.querySelector('swatches-variant-picker-component');
  }
  /** @type {number | null} */
  #previousSlideIndex = null;

  /**
   * Handles the slideshow select event.
   * @param {SlideshowSelectEvent} event - The slideshow select event.
   */
  #handleSlideshowSelect = (event) => {
    if (event.detail.userInitiated) {
      this.#previousSlideIndex = event.detail.index;
    }
  };

  /**
   * Previews a variant.
   * @param {string} id - The id of the variant to preview.
   */
  async previewVariant(id) {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.#userChangedVariant = true;
    this.#markGalleryReady();
    this.resetVariant.cancel();

    // FIX: swatch without a variant image (empty media id) -> keep the current image
    if (id === undefined || id === null || id === '' || !this.#getSlideById(id)) return;

    // FIX: on mobile, a tap fires "pointerenter" too, which used to jump instantly to a hidden
    // slide whose lazy image had never been downloaded -> white empty image.
    // Now we wait for the image first, and keep the current image visible meanwhile.
    const token = ++this.#previewToken;
    await this.#waitForSlideImage(this.#getSlideById(id));
    if (token !== this.#previewToken) return;

    slideshow.select({ id }, undefined, { animate: false });
  }

  /**
   * Previews the next image.
   * @param {PointerEvent} event - The pointer event.
   */
  previewImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.#userChangedVariant = true;
    this.#markGalleryReady();
    this.resetVariant.cancel();

    if (this.#previousSlideIndex != null && this.#previousSlideIndex > 0) {
      slideshow.select(this.#previousSlideIndex, undefined, { animate: false });
    } else {
      slideshow.next(undefined, { animate: false });
      setTimeout(() => this.#preloadNextPreviewImage());
    }
  }

  /**
   * Resets the image to the variant image.
   * @param {PointerEvent} event - The pointer event.
   */
  resetImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;

    if (!this.variantPicker) {
      if (!slideshow) return;
      slideshow.previous(undefined, { animate: false });
    } else {
      this.#resetVariant();
    }
  }

  /**
   * Resets the image to the variant image.
   */
  #resetVariant = () => {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    // Cancel any preview that is still waiting for its image
    this.#previewToken++;

    // FIX: go back to the image of the selected swatch first (if it has one).
    const id = this.variantPicker?.selectedOption?.dataset.optionMediaId;
    if (id && this.#getSlideById(id)) {
      slideshow.select({ id }, undefined, { animate: false });
      return;
    }

    const initialSlide = slideshow.initialSlide;
    const slideId = initialSlide?.getAttribute('slide-id');
    if (initialSlide && slideshow.slides?.includes(initialSlide) && slideId) {
      slideshow.select({ id: slideId }, undefined, { animate: false });
      return;
    }

    // FIX: used to be slideshow.previous(), which on swatches without a variant image (e.g. Crewneck "Black")
    // jumped backwards to the LAST photo of the gallery (lazy, not loaded) -> white / random image.
    slideshow.select(0, undefined, { animate: false });
  };

  /**
   * Intercepts the click event on the product card anchor, we want
   * to use this to add an intermediate state to the history.
   * This intermediate state captures the page we were on so that we
   * navigate back to the same page when the user navigates back.
   * In addition to that, it captures the product card anchor so that we
   * have the specific product card in view.
   *
   * A product card can have other interactive elements like variant picker,
   * so we do not navigate if the click was on one of those elements.
   *
   * @param {Event} event
   */
  navigateToProduct = (event) => {
    if (!(event.target instanceof Element)) return;

    // Don't navigate if this product card is marked as no-navigation (e.g., in theme editor)
    if (this.hasAttribute('data-no-navigation')) return;

    const interactiveElement = event.target.closest('button, input, label, select, a, [tabindex="1"]');

    // If the click was on an interactive element which is not the main link, do nothing.
    if (interactiveElement && interactiveElement !== this.refs.productCardLink) {
      return;
    }

    const link = this.refs.productCardLink;
    if (!link.href) return;
    const linkURL = new URL(link.href);

    const productCardAnchor = link.getAttribute('id');
    if (!productCardAnchor) return;

    const url = new URL(window.location.href);
    const parent = this.closest('li');
    url.hash = productCardAnchor;
    if (parent && parent.dataset.page) {
      url.searchParams.set('page', parent.dataset.page);
    }

    if (!window.Shopify.designMode) {
      history.replaceState({}, '', url.toString());
    }

    this.#navigateToURL(event, linkURL);
  };

  /**
   * Resets the variant.
   */
  resetVariant = debounce((event) => {
    // FIX: on touch devices "pointerleave" fires right after every tap. Resetting there made the image jump
    // back to the old color (or to a random slide) before the new color loaded. Only reset for a real mouse.
    if (event && typeof event.pointerType === 'string' && event.pointerType !== 'mouse') return;
    this.#resetVariant();
  }, 100);
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}

/**
 * A custom element that displays a variant picker with swatches.
 *
 * @typedef {object} SwatchesRefs
 * @property {HTMLElement} overflowList
 *
 * @extends {VariantPicker<SwatchesRefs>}
 */
class SwatchesVariantPickerComponent extends VariantPicker {
  /**
   * Shows all swatches.
   * @param {Event} [event] - The event that triggered the show all swatches.
   */
  showAllSwatches(event) {
    event?.preventDefault();

    const { overflowList } = this.refs;

    if (overflowList instanceof OverflowList) {
      overflowList.showAll();
    }
  }
}

if (!customElements.get('swatches-variant-picker-component')) {
  customElements.define('swatches-variant-picker-component', SwatchesVariantPickerComponent);
}
